import { IInstitution } from '@/modules/portfolio/accountModal/accountTypeData';
import { useLazyGetYodleeTokenQuery, YodleeToken } from '@/store/yodleeApi';
import { isCapacitorApp } from '@/utils/isCapacitorApp';
import { Browser } from '@capacitor/browser';
import { Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';

export interface YodleeErrorDetails {
  ActionType?: string;
  [key: string]: unknown;
}

export interface YodleeErrorData {
  sites?: Array<{
    providerAccountId?: string | number;
    providerId?: string | number;
    status?: string;
    statusCode?: string;
    reason?: string;
    requestId?: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

export interface YodleeExitData {
  fnToCall?: string;
  status?: string;
  sites?: Array<{
    providerAccountId?: string | number;
    providerId?: string | number;
    status?: string;
    statusCode?: string;
    reason?: string;
    requestId?: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

export interface YodleeSuccessData {
  providerAccountId?: string | number;
  providerId?: string | number;
  requestId?: string;
  sites?: Array<{
    providerAccountId?: string | number;
    providerId?: string | number;
    status?: string;
    statusCode?: string;
    reason?: string;
    requestId?: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

/**
 * - Flows: https://developer.yodlee.com/resources/yodlee/upgrade-guide/docs/launch-fastlink-deeplink-flows
 * - Product Flows / Iframe Mounted: https://developer.yodlee.com/resources/yodlee/fastlink-3/docs/documentation
 * - Advanced Integration: https://developer.yodlee.com/resources/yodlee/fastlink-3/docs/advanced-integration
 */
export interface YodleeParams {
  providerId?: string;
  providerAccountId?: string | number;
  flow?: 'add' | 'edit' | 'refresh' | 'verify';
  isIFrameMounted?: boolean;
  intentUrl?: string;
  userExperienceFlow?:
    | 'Aggregation'
    | 'Verification'
    | 'Aggregation plus Verification';
}

interface FastLinkIntegrationProps {
  onError: (
    data: YodleeSuccessData,
    institution: IInstitution,
    yodleeToken: YodleeToken,
  ) => void;
  onSuccess: (
    data: YodleeSuccessData,
    institution: IInstitution,
    yodleeToken: YodleeToken,
  ) => Promise<void>;
  onClose: (
    data: YodleeExitData,
    institution: IInstitution,
    yodleeToken: YodleeToken,
  ) => void;
  /** Fired when token fetch, script load, or FastLink open is still in progress. */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Fired when FastLink closes (success, error, or exit) before parent UI updates. */
  onSessionProcessing?: () => void;
  /** Fired when the main tab regains visibility while awaiting an external OAuth redirect return (web only). */
  onExternalOAuthReturn?: () => void;
  /** Fired when the external OAuth safety timeout elapses with no yodlee.js confirmation (web only). */
  onExternalOAuthTimeout?: () => void;
  params?: YodleeParams;
  institution: IInstitution;
}

const FastLinkIntegration = ({
  onError,
  onSuccess,
  onClose,
  onLoadingChange,
  onSessionProcessing,
  onExternalOAuthReturn,
  onExternalOAuthTimeout,
  params,
  institution,
}: FastLinkIntegrationProps) => {
  const { t } = useLocalization();

  const [getYodleeToken, { data: yodleeToken, isLoading, error }] =
    useLazyGetYodleeTokenQuery();
  const [isYodleeError, setYodleeError] = useState<boolean>(false);
  const [isWaitingForScript, setIsWaitingForScript] = useState<boolean>(false);
  const [hasOpenedFastLink, setHasOpenedFastLink] = useState<boolean>(false);

  const timeoutRef = useRef<number | null>(null);
  const fastLinkInstanceRef = useRef<boolean>(false);
  const isBrowserOpenRef = useRef<boolean>(false);
  const awaitingExternalReturnRef = useRef<boolean>(false);
  const externalReturnTimeoutRef = useRef<number | null>(null);
  const onExternalOAuthReturnRef = useRef(onExternalOAuthReturn);
  const onExternalOAuthTimeoutRef = useRef(onExternalOAuthTimeout);
  onExternalOAuthReturnRef.current = onExternalOAuthReturn;
  onExternalOAuthTimeoutRef.current = onExternalOAuthTimeout;

  const EXTERNAL_OAUTH_TIMEOUT_MS = 15000;

  const CONTAINER_ID = 'container-fastlink';
  const SCRIPT_ID = 'yodlee-fastlink-script';

  const isSessionLoading =
    !(error || isYodleeError) &&
    (isLoading || !yodleeToken || isWaitingForScript || !hasOpenedFastLink);

  useEffect(() => {
    onLoadingChange?.(isSessionLoading);
  }, [isSessionLoading, onLoadingChange]);

  useEffect(() => {
    void getYodleeToken();
  }, [getYodleeToken]);

  useEffect(() => {
    addFastLinkScriptToDOM();
  }, []);

  useEffect(() => {
    if (yodleeToken) {
      loadFastLink();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // window.fastlink is a page-level singleton owned by yodlee.js: close()
      // is the only way to reset its internal fastLinkOpened flag. Destroying
      // the global instead would force a script reload on the next mount,
      // stacking stale message listeners that act on future sessions.
      if (window.fastlink && fastLinkInstanceRef.current) {
        try {
          window.fastlink.close();
        } catch (err: unknown) {
          console.log('Error closing FastLink on unmount:', err);
        }
      }
      fastLinkInstanceRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yodleeToken]);

  useEffect(() => {
    if (!isCapacitorApp()) return;
    let handle: { remove: () => void } | null = null;
    void Browser.addListener('browserFinished', () => {
      isBrowserOpenRef.current = false;
    }).then((h) => {
      handle = h;
    });
    return () => {
      handle?.remove();
    };
  }, []);

  useEffect(() => {
    return () => {
      clearExternalOAuthWatch();
    };
  }, []);

  const closeBrowserIfNeeded = () => {
    if (isCapacitorApp() && isBrowserOpenRef.current) {
      void Browser.close().catch((err: unknown) => {
        console.error('Error closing browser:', err);
      });
      isBrowserOpenRef.current = false;
    }
  };

  const clearExternalOAuthWatch = () => {
    awaitingExternalReturnRef.current = false;
    document.removeEventListener(
      'visibilitychange',
      handleExternalReturnVisibilityRef.current,
    );
    window.removeEventListener(
      'focus',
      handleExternalReturnVisibilityRef.current,
    );
    if (externalReturnTimeoutRef.current) {
      window.clearTimeout(externalReturnTimeoutRef.current);
      externalReturnTimeoutRef.current = null;
    }
  };

  // Wrapped in a ref so the SAME function instance is used for every
  // addEventListener/removeEventListener call across the component's whole
  // lifetime, regardless of which render triggers them (renderFastLink runs
  // only once yodleeToken resolves, which can be a later render than mount).
  // A stale reference here would mean removeEventListener silently fails to
  // match, leaking the listener past unmount.
  const handleExternalReturnVisibilityRef = useRef(() => {
    if (
      document.visibilityState !== 'visible' ||
      !awaitingExternalReturnRef.current
    ) {
      return;
    }
    awaitingExternalReturnRef.current = false;
    document.removeEventListener(
      'visibilitychange',
      handleExternalReturnVisibilityRef.current,
    );
    window.removeEventListener(
      'focus',
      handleExternalReturnVisibilityRef.current,
    );
    onExternalOAuthReturnRef.current?.();
    externalReturnTimeoutRef.current = window.setTimeout(() => {
      externalReturnTimeoutRef.current = null;
      onExternalOAuthTimeoutRef.current?.();
    }, EXTERNAL_OAUTH_TIMEOUT_MS);
  });

  /**
   * Adds the FastLink script to the DOM
   */
  const addFastLinkScriptToDOM = () => {
    // Guard by script tag, not by window.fastlink: the global can be briefly
    // unset while the script is still loading, and re-adding the script would
    // register duplicate global message listeners that never get removed.
    if (document.getElementById(SCRIPT_ID)) {
      return;
    }
    const script = document.createElement('script');
    const librariesBaseUrl =
      import.meta.env.VITE_LIBRARIES_URL ??
      'https://assets.wealthaccess.com/libs/yodlee.js';
    const normalizedBaseUrl = librariesBaseUrl.replace(/\/$/, '');
    script.id = SCRIPT_ID;
    script.src = normalizedBaseUrl;
    script.async = true;
    // A dead tag would block every retry behind the SCRIPT_ID guard.
    script.onerror = () => {
      script.remove();
    };
    document.body.appendChild(script);
  };

  /**
   * Currently scoped to "Add"
   */
  const buildParams = (institution: IInstitution): YodleeParams => {
    const native = isCapacitorApp();
    const legacyLoginUrl = import.meta.env.VITE_LEGACY_LOGIN_URL ?? '';
    const intentBase = legacyLoginUrl ? new URL(legacyLoginUrl).origin : '';
    return {
      flow: 'add',
      userExperienceFlow: 'Aggregation',
      isIFrameMounted: native,
      providerId: institution.institution?.InstitutionId,
      ...(native &&
        intentBase && {
          intentUrl: `${intentBase}/Scripts/Pages/loading.html`,
        }),
      ...params,
    };
  };

  /** Ensure that fastlink has been loaded */
  const loadFastLink = () => {
    if (!window.fastlink) {
      console.log('fastlink does not exist');
      setIsWaitingForScript(true);
      timeoutRef.current = window.setTimeout(loadFastLink, 200);
      return;
    }

    setIsWaitingForScript(false);
    renderFastLink();
  };

  /**
   * Initialize the FastLink v3
   */
  const renderFastLink = () => {
    if (!yodleeToken || fastLinkInstanceRef.current) {
      console.log('NOT RENDERING FAST LINK');
      return;
    }

    console.log('RENDER FAST LINK');
    fastLinkInstanceRef.current = true;

    // Defensive close: yodlee.js refuses to open ("FastLink already in use")
    // while its internal fastLinkOpened flag is set, and an aborted session
    // (e.g. an exit with FAILED status) can leave it set with no UI mounted.
    try {
      window.fastlink.close();
    } catch (err: unknown) {
      console.log('Error closing stale FastLink session:', err);
    }

    try {
      window.fastlink.open(
        {
          fastLinkURL: import.meta.env.VITE_YODLEE_FASTLINK_URL ?? '',
          jwtToken: `Bearer ${yodleeToken.token}`,
          params: buildParams(institution),
          forceIframe: true,
          iframeResize: true,
          onSuccess: (data: Record<string, unknown>) => {
            console.log('onSuccess', data);
            onSessionProcessing?.();
            clearExternalOAuthWatch();
            closeBrowserIfNeeded();
            void onSuccess(data, institution, yodleeToken);
          },
          onError: (data: Record<string, unknown>) => {
            console.log('onError', data);
            onSessionProcessing?.();
            clearExternalOAuthWatch();
            setYodleeError(true);
            closeBrowserIfNeeded();
            onError(data, institution, yodleeToken);
          },
          onExit: (data: Record<string, unknown>) => {
            console.log('onExit', data);
            onSessionProcessing?.();
            clearExternalOAuthWatch();
            fastLinkInstanceRef.current = false;
            closeBrowserIfNeeded();
            onClose(data, institution, yodleeToken);
          },
          onEvent: (event: Record<string, unknown>) => {
            console.log('onEvent', event);
            if (event.type === 'OPEN_EXTERNAL_URL') {
              const eventData = event.data;
              let url = '';
              if (eventData !== null && typeof eventData === 'object') {
                const record = eventData as Record<string, unknown>;
                const maybeUrl = record['url'];
                if (typeof maybeUrl === 'string') {
                  url = maybeUrl;
                }
              }
              console.log('OPEN_EXTERNAL_URL', url);
              if (isCapacitorApp()) {
                isBrowserOpenRef.current = true;
                void Browser.open({ url }).catch((err: unknown) => {
                  console.error('Error opening external browser:', err);
                  isBrowserOpenRef.current = false;
                });
              } else {
                const externalWindow = window.open(url, '_blank');
                if (externalWindow) {
                  awaitingExternalReturnRef.current = true;
                  document.addEventListener(
                    'visibilitychange',
                    handleExternalReturnVisibilityRef.current,
                  );
                  // Some desktop browser/OS combinations leave the opener
                  // tab's document.visibilityState as 'visible' the whole
                  // time a window.open('_blank') popup is open (no tab
                  // occlusion), so visibilitychange never fires. window
                  // regaining OS-level focus is an independent signal that
                  // catches that case; whichever fires first wins via the
                  // awaitingExternalReturnRef gate inside the shared handler.
                  window.addEventListener(
                    'focus',
                    handleExternalReturnVisibilityRef.current,
                  );
                }
              }
            }
          },
        },
        CONTAINER_ID,
      );
      setHasOpenedFastLink(true);
    } catch (error) {
      console.log('renderFastLink error :>> ', error);
      setYodleeError(true);
    }
  };

  if (error || isYodleeError) {
    return (
      <Stack
        height='300px'
        justifyContent='center'
        alignItems='center'
        gap={2}
      >
        <Typography color='error'>
          {t('fastLinkIntegration.connectionError')}
        </Typography>
      </Stack>
    );
  }

  return (
    <div
      id={CONTAINER_ID}
      style={{ minHeight: '500px', width: '100%' }}
    ></div>
  );
};

export default FastLinkIntegration;
